// model.ts — Polymorphic planet behaviour layer. The wire `Planet` (types.ts) is a
// plain JSON value shared verbatim with the Go server + valkey, so it stays a flat
// interface. This module wraps one of those values in a kind-specific subclass that
// owns everything the renderer and UI used to branch on by `kind` (ocean/clouds,
// the lava glow, the display label) plus a derived CLIMATE — day length and average
// day/night temperatures computed from the host star and orbit.
//
//   const model = planetModel(planet);
//   model.hasOcean, model.hasClouds, model.label, model.climate(system)
import type { Planet, PlanetKind, StarSystem } from '../../types';
import { SPECTRAL } from '../../galaxy/spectral';

export interface OceanTint {
  shallow: string;
  deep: string;
}

export interface Climate {
  /** Solar-day length in game-hours, or null when tidally locked (no day/night). */
  dayLengthHours: number | null;
  meanTempC: number;
  /** Day-side (sunlit) average; the permanent lit face when tidally locked. */
  dayTempC: number;
  /** Night-side average; the permanent dark face when tidally locked. */
  nightTempC: number;
}

const ABS_ZERO_C = -273.15;
// A dark/night face radiates toward an equilibrium deep-cold floor (~40 K) rather
// than all the way to absolute zero.
const NIGHT_FLOOR_C = -235;

// Stable per-planet rotation period (game-hours), 8–44h, from an FNV-1a hash of id.
function rotationHours(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return 8 + ((h >>> 0) % 1000) / 1000 * 36;
}

// Per-class physical + visual properties. Data-driven (one row per Stellaris class)
// rather than a subclass each: the climate model reads albedo/greenhouse/swing/
// internal-heat here, and the renderer reads clouds/ocean/emissive. The colours are
// produced by the palette panel and mirror presets.ts / Atmosphere.tsx.
export interface KindProps {
  label: string;
  /** Bond albedo (0 = black, 1 = mirror) — reflective ice cools, dark lava heats. */
  albedo: number;
  /** Additive greenhouse warming (°C) from the atmosphere. */
  greenhouseC: number;
  /** Half-width of the diurnal temperature swing; thin air swings harder. */
  diurnalSwingC: number;
  /** Non-stellar internal heat (molten/volcanic run hot regardless of the star). */
  extraWarmthC: number;
  hasClouds: boolean;
  /** Fraction of the disc under cloud (0 = clear skies, 1 = fully shrouded). Wet
   *  worlds (ocean, continental, tropical) run high; dry ones (arid, desert, tundra,
   *  volcanic) low; a toxic world is socked in. Drives the cloud coverage threshold
   *  in Clouds.tsx. Ignored when hasClouds is false. */
  cloudCover: number;
  /** Cloud-deck tint, or null when the world has no atmosphere/clouds. */
  cloudColor: string | null;
  /** Liquid-ocean shell tint, or null for dry/frozen/airless worlds. */
  ocean: OceanTint | null;
  /** Self-luminous surface (lava cracks, molten seas, fracture glow), else null. */
  emissive: { color: string; intensity: number } | null;
}

export const KIND_PROPS: Record<PlanetKind, KindProps> = {
  tropical: { label: 'Tropical', albedo: 0.28, greenhouseC: 34, diurnalSwingC: 6, extraWarmthC: 0, hasClouds: true, cloudCover: 0.72, cloudColor: '#ffffff', ocean: { shallow: '#1fb6c9', deep: '#062b4a' }, emissive: null },
  continental: { label: 'Continental', albedo: 0.31, greenhouseC: 28, diurnalSwingC: 7, extraWarmthC: 0, hasClouds: true, cloudCover: 0.62, cloudColor: '#ffffff', ocean: { shallow: '#2378a0', deep: '#0a2a4f' }, emissive: null },
  ocean: { label: 'Ocean', albedo: 0.27, greenhouseC: 24, diurnalSwingC: 5, extraWarmthC: 0, hasClouds: true, cloudCover: 0.7, cloudColor: '#ffffff', ocean: { shallow: '#2aa6c4', deep: '#04203f' }, emissive: null },
  savanna: { label: 'Savanna', albedo: 0.32, greenhouseC: 28, diurnalSwingC: 9, extraWarmthC: 0, hasClouds: true, cloudCover: 0.42, cloudColor: '#e2cfa0', ocean: { shallow: '#3f8a8a', deep: '#1f4a5a' }, emissive: null },
  arid: { label: 'Arid', albedo: 0.27, greenhouseC: 30, diurnalSwingC: 14, extraWarmthC: 0, hasClouds: true, cloudCover: 0.18, cloudColor: '#d2b896', ocean: { shallow: '#8a5a32', deep: '#5a3a22' }, emissive: null },
  desert: { label: 'Desert', albedo: 0.35, greenhouseC: 10, diurnalSwingC: 28, extraWarmthC: 0, hasClouds: true, cloudCover: 0.12, cloudColor: '#ddc89a', ocean: null, emissive: null },
  arctic: { label: 'Arctic', albedo: 0.58, greenhouseC: 12, diurnalSwingC: 10, extraWarmthC: 0, hasClouds: true, cloudCover: 0.5, cloudColor: '#e6eef2', ocean: null, emissive: null },
  tundra: { label: 'Tundra', albedo: 0.4, greenhouseC: 16, diurnalSwingC: 12, extraWarmthC: 0, hasClouds: true, cloudCover: 0.2, cloudColor: '#d8dde0', ocean: null, emissive: null },
  alpine: { label: 'Alpine', albedo: 0.4, greenhouseC: 14, diurnalSwingC: 10, extraWarmthC: 0, hasClouds: true, cloudCover: 0.46, cloudColor: '#ffffff', ocean: { shallow: '#5aa0b0', deep: '#2f5a6a' }, emissive: null },
  volcanic: { label: 'Volcanic', albedo: 0.14, greenhouseC: 42, diurnalSwingC: 11, extraWarmthC: 220, hasClouds: true, cloudCover: 0.14, cloudColor: '#5a5450', ocean: null, emissive: { color: '#ff5a14', intensity: 0.45 } },
  barren_dry: { label: 'Barren (Dry)', albedo: 0.18, greenhouseC: 0, diurnalSwingC: 45, extraWarmthC: 0, hasClouds: false, cloudCover: 0, cloudColor: null, ocean: null, emissive: null },
  barren_cold: { label: 'Barren (Cold)', albedo: 0.16, greenhouseC: 0, diurnalSwingC: 48, extraWarmthC: 0, hasClouds: false, cloudCover: 0, cloudColor: null, ocean: null, emissive: null },
  broken: { label: 'Broken', albedo: 0.13, greenhouseC: 0, diurnalSwingC: 46, extraWarmthC: 0, hasClouds: false, cloudCover: 0, cloudColor: null, ocean: null, emissive: { color: '#7a2410', intensity: 0.22 } },
  frozen: { label: 'Frozen', albedo: 0.62, greenhouseC: 2, diurnalSwingC: 30, extraWarmthC: 0, hasClouds: false, cloudCover: 0, cloudColor: null, ocean: null, emissive: null },
  gas: { label: 'Gas Giant', albedo: 0.5, greenhouseC: 0, diurnalSwingC: 0, extraWarmthC: 0, hasClouds: false, cloudCover: 0, cloudColor: null, ocean: null, emissive: null },
  molten: { label: 'Molten', albedo: 0.1, greenhouseC: 60, diurnalSwingC: 5, extraWarmthC: 450, hasClouds: true, cloudCover: 0.2, cloudColor: '#4a3f3a', ocean: null, emissive: { color: '#ff7a1e', intensity: 0.6 } },
  toxic: { label: 'Toxic', albedo: 0.34, greenhouseC: 75, diurnalSwingC: 5, extraWarmthC: 0, hasClouds: true, cloudCover: 0.95, cloudColor: '#c4d65a', ocean: { shallow: '#8aa83a', deep: '#3a4a1c' }, emissive: null },
};

// The classes that can host a resident population: the temperate habitable span
// that also carries liquid oceans (so cities have a coastline to sit on). The dead
// (barren/broken/frozen/gas), the violent (volcanic/molten), the airless-poisoned
// (toxic), the waterless (desert), and the frozen-sea worlds (arctic/tundra) are
// excluded. SINGLE SOURCE OF TRUTH on the client — the galaxy generator reads it to
// decide which worlds get pops, and cities.ts to lay them out. The Go generator
// mirrors it (server/galaxy/galaxy.go: habitableKinds).
export const HABITABLE_KINDS: ReadonlySet<PlanetKind> = new Set<PlanetKind>([
  'ocean',
  'tropical',
  'continental',
  'savanna',
  'arid',
  'alpine',
]);

/** A planet kind can host pops in coastal cities. */
export function isHabitable(kind: PlanetKind): boolean {
  return HABITABLE_KINDS.has(kind);
}

export class PlanetModel {
  readonly props: KindProps;

  constructor(readonly data: Planet) {
    this.props = KIND_PROPS[data.kind];
  }

  /** Human-readable class, e.g. "Tropical". */
  get label(): string {
    return this.props.label;
  }
  get hasClouds(): boolean {
    return this.props.hasClouds;
  }
  /** Cloud-deck tint for this class (null when it has no clouds). */
  get cloudColor(): string | null {
    return this.props.cloudColor;
  }
  /** Fraction of the disc under cloud (0..1) — drives Clouds' coverage threshold. */
  get cloudCover(): number {
    return this.props.cloudCover;
  }
  get oceanTint(): OceanTint | null {
    return this.props.ocean;
  }
  get hasOcean(): boolean {
    return this.props.ocean !== null;
  }
  /** Can host a resident population in coastal cities (see HABITABLE_KINDS). */
  get habitable(): boolean {
    return isHabitable(this.data.kind);
  }
  /** This world's resident population in millions (1 pop = 1,000,000 people). */
  get pops(): number {
    return this.data.pops ?? 0;
  }
  /** Self-luminous surface (lava) for the standard material, else null. */
  get emissive(): { color: string; intensity: number } | null {
    return this.props.emissive;
  }
  get surfaceRoughness(): number {
    return 0.9;
  }

  /** Day length in game-hours; null = tidally locked (one face always to the star). */
  get dayLengthHours(): number | null {
    return this.data.tidallyLocked ? null : rotationHours(this.data.id);
  }

  /** Average day/night temperatures from the host star's flux and this orbit. */
  climate(star: StarSystem): Climate {
    const lum = SPECTRAL[star.spectralClass].lum;
    const a = this.data.semiMajorAxis;
    // Empirical base temp — the SAME warm-inner→cold-outer + star-shift curve the
    // generator uses to pick the class (so the shown temperature always agrees with
    // the rendered world), then per-class albedo (bright worlds cooler), greenhouse,
    // and internal heat (molten/volcanic).
    const base = 45 - 80 * Math.log2(a / 2) + 30 * Math.tanh(0.5 * Math.log2(lum));
    const meanC =
      base - 80 * (this.props.albedo - 0.3) + this.props.greenhouseC + this.props.extraWarmthC;
    // A tidally-locked world has no diurnal cycle — instead a permanently scorched
    // dayside and a frozen nightside, a far larger contrast than a rotation swing.
    const swing = this.data.tidallyLocked
      ? this.props.diurnalSwingC * 2.5 + 45
      : this.props.diurnalSwingC;
    return {
      dayLengthHours: this.dayLengthHours,
      meanTempC: Math.round(Math.max(meanC, ABS_ZERO_C)),
      dayTempC: Math.round(Math.max(meanC + swing, ABS_ZERO_C)),
      nightTempC: Math.round(Math.max(meanC - swing, NIGHT_FLOOR_C)),
    };
  }
}

/** Wrap a wire Planet in its kind-specific model. */
export function planetModel(data: Planet): PlanetModel {
  return new PlanetModel(data);
}

/** A moon's display name: the parent planet's name plus a lowercase letter by index,
 *  e.g. the first moon of "Sol III" is "Sol IIIa", the second "Sol IIIb". */
export function moonName(planetName: string, index: number): string {
  return `${planetName}${String.fromCharCode(97 + index)}`;
}
